import { NavLink } from 'react-router-dom';
import {
  FileText,
  Flame,
  LayoutDashboard,
  Megaphone,
  Package,
  PhoneCall,
  ScrollText,
  Settings,
  ShieldCheck,
  Tags,
  UserCog,
  Users,
  Wrench,
  type LucideIcon,
  Mail,
} from 'lucide-react';
import { hasPermission, type PermissionKey } from '@fenix/shared';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useAuthStore } from '@/features/auth/store';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: PermissionKey;
  /** Só o Super Administrador vê (ferramentas fora do catálogo de permissões). */
  superAdminOnly?: boolean;
}

const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Operação',
    items: [
      { to: '/dashboard', label: 'Painel', icon: LayoutDashboard, permission: 'dashboard.read' },
      { to: '/leads', label: 'Leads', icon: Users, permission: 'leads.read' },
      { to: '/campanhas', label: 'Campanhas', icon: Megaphone, permission: 'campaigns.read' },
      { to: '/chamadas', label: 'Chamadas', icon: PhoneCall, permission: 'calls.read' },
      { to: '/emails', label: 'E-mails', icon: Mail, permission: 'calls.read' },
    ],
  },
  {
    label: 'Configuração',
    items: [
      { to: '/scripts', label: 'Scripts', icon: FileText, permission: 'scripts.read' },
      { to: '/produtos', label: 'Produtos', icon: Package, permission: 'products.read' },
      { to: '/marcas', label: 'Marcas', icon: Tags, permission: 'products.read' },
      { to: '/configuracoes', label: 'Configurações', icon: Settings, permission: 'settings.read' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: UserCog, permission: 'users.read' },
      { to: '/perfis', label: 'Perfis e permissões', icon: ShieldCheck, permission: 'roles.read' },
      { to: '/auditoria', label: 'Auditoria', icon: ScrollText, permission: 'audit.read' },
      { to: '/ferramentas', label: 'Ferramentas', icon: Wrench, superAdminOnly: true },
    ],
  },
];

export function AppSidebar() {
  const user = useAuthStore((s) => s.user);
  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) =>
      i.superAdminOnly
        ? user?.role === 'super_admin'
        : !i.permission || hasPermission(user, i.permission),
    ),
  })).filter((g) => g.items.length);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Flame className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Fênix</span>
                  <span className="truncate text-xs text-muted-foreground">Call Center IA</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <NavLink to={item.to}>
                      {({ isActive }) => (
                        <SidebarMenuButton isActive={isActive} tooltip={item.label}>
                          <item.icon />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <p className="px-2 text-[10px] text-muted-foreground group-data-[collapsible=icon]:hidden">
          v0.1.0 · Grupo Fênix
        </p>
      </SidebarFooter>
    </Sidebar>
  );
}
